import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Button, 
  Card, CardContent, CardActions, 
  Dialog, DialogActions, DialogContent, DialogTitle,
  CircularProgress, Alert, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem,
  keyframes, styled
} from '@mui/material';
import { QrCodeScanner, CheckCircle } from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import { formatDate } from '../../utils/dateUtils';
import { QrReader } from 'react-qr-reader';

// Определение анимации пульсации
const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 4000px rgba(0, 0, 0, 0.5), 0 0 0 0 rgba(52, 152, 219, 0.7); }
  70% { box-shadow: 0 0 0 4000px rgba(0, 0, 0, 0.5), 0 0 0 10px rgba(52, 152, 219, 0); }
  100% { box-shadow: 0 0 0 4000px rgba(0, 0, 0, 0.5), 0 0 0 0 rgba(52, 152, 219, 0); }
`;

// Стилизованный компонент рамки сканера
const ScannerBorder = styled(Box)(({ theme }) => ({
  border: '3px solid #3498db',
  width: '70%',
  height: '70%',
  boxShadow: '0 0 0 4000px rgba(0, 0, 0, 0.5)',
  borderRadius: '8px',
  animation: `${pulseAnimation} 2s infinite`
}));

const ScanCoupon = () => {
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('environment');
  
  const { user: userInfo } = useAuthStore();

  // Функция для перевода сообщений об ошибках с английского на русский
  const translateErrorMessage = (message) => {
    if (!message) return 'Ошибка при проверке купона';
    
    // Словарь для перевода типичных ошибок
    const errorTranslations = {
      'Coupon not found': 'Купон не найден',
      'Invalid QR code': 'Недействительный QR-код',
      'Coupon already used': 'Купон уже использован',
      'Coupon expired': 'Срок действия купона истек',
      'Coupon is inactive': 'Купон неактивен',
      'User not found': 'Пользователь не найден',
      'Not authorized': 'Нет прав доступа',
      'Invalid token': 'Недействительный токен авторизации'
    };
    
    // Проверяем, есть ли перевод для данного сообщения
    return errorTranslations[message] || message;
  };

  const handleScan = async (result) => {
    if (result && result.text && !loading) {
      setLoading(true);
      setScanning(false);
      
      try {
        const response = await axios.get(`/api/coupons/verify/${result.text}`, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        
        setCouponData(response.data);
        setConfirmDialog(true);
        setError(null);
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Ошибка при проверке купона';
        setError(translateErrorMessage(errorMessage));
        setCouponData(null);
        console.error('Error verifying coupon:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleError = (err) => {
    console.error('QR Scanner Error:', err);
    setError('Ошибка при сканировании QR-кода. Пожалуйста, попробуйте еще раз.');
    setScanning(false);
  };

  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setCameras(videoDevices);
        
        // Если есть задняя камера, выбираем её по умолчанию
        const backCamera = videoDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('задн'));
          
        if (backCamera) {
          setSelectedCamera(backCamera.deviceId);
        }
      } catch (err) {
        console.error('Error getting cameras:', err);
      }
    };
    
    if (scanning) {
      getCameras();
    }
  }, [scanning]);

  const handleStartScan = () => {
    setScanning(true);
    setError(null);
    setSuccess(null);
  };
  
  const handleCameraChange = (event) => {
    setSelectedCamera(event.target.value);
  };

  const handleCancelScan = () => {
    setScanning(false);
  };

  const handleConfirmUse = async () => {
    if (!couponData) return;
    
    setLoading(true);
    try {
      await axios.post(`/api/coupons/use/${couponData.qrCode}`, {}, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      
      setSuccess(`Купон "${couponData.coupon.name}" успешно использован!`);
      setCouponData(null);
      setConfirmDialog(false);
      
      // Прокрутка к сообщению об успехе
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 300);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Ошибка при использовании купона';
      setError(translateErrorMessage(errorMessage));
      console.error('Error using coupon:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseConfirm = () => {
    setConfirmDialog(false);
  };

  return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Сканирование купонов
        </Typography>
        
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="body1" paragraph align="center">
            Нажмите кнопку "Сканировать QR-код" и наведите камеру на QR-код купона клиента.
            После сканирования вы сможете подтвердить использование купона.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<QrCodeScanner />}
              onClick={handleStartScan}
              disabled={scanning || loading}
              size="large"
            >
              Сканировать QR-код
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 3, py: 2 }}
              icon={<CheckCircle fontSize="inherit" />}
              variant="filled"
            >
              <Typography variant="body1">{success}</Typography>
            </Alert>
          )}
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>
        
        {/* QR Scanner Dialog */}
        <Dialog 
          open={scanning} 
          onClose={handleCancelScan}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { minHeight: { xs: '80vh', sm: '60vh' } }
          }}
        >
          <DialogTitle>Сканирование QR-кода</DialogTitle>
          <DialogContent>
            {cameras.length > 1 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="camera-select-label">Выберите камеру</InputLabel>
                <Select
                  labelId="camera-select-label"
                  value={selectedCamera}
                  onChange={handleCameraChange}
                  label="Выберите камеру"
                >
                  <MenuItem value="environment">Задняя камера (по умолчанию)</MenuItem>
                  <MenuItem value="user">Фронтальная камера</MenuItem>
                  {cameras.map((camera) => (
                    <MenuItem key={camera.deviceId} value={camera.deviceId}>
                      {camera.label || `Камера ${camera.deviceId.slice(0, 5)}...`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <Box sx={{ position: 'relative' }}>
              <QrReader
                constraints={{
                  facingMode: selectedCamera === 'environment' ? 'environment' : 
                              selectedCamera === 'user' ? 'user' : 
                              { deviceId: selectedCamera }
                }}
                onResult={handleScan}
                videoStyle={{ width: '100%', height: '50vh', objectFit: 'cover' }}
                videoId="qr-reader-video"
                scanDelay={500}
              />
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none'
                }}
              >
                <ScannerBorder />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelScan} color="primary">
              Отмена
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Confirmation Dialog */}
        <Dialog 
          open={confirmDialog} 
          onClose={handleCloseConfirm}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Подтверждение использования купона</DialogTitle>
          <DialogContent>
            {couponData && (
              <Card sx={{ mb: 2, ...(couponData.isUsed && { border: '2px solid #f44336' }) }}>
                <CardContent>
                  {couponData.isUsed && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Внимание! Этот купон уже был использован {new Date(couponData.usedAt).toLocaleDateString()} в {new Date(couponData.usedAt).toLocaleTimeString()}
                    </Alert>
                  )}
                  <Typography variant="h6" gutterBottom>
                    {couponData.coupon.name}
                  </Typography>
                  
                  <Typography variant="body1" color="primary" gutterBottom>
                    Сумма: {couponData.coupon.value} ₽
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" gutterBottom>
                    Информация о клиенте:
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Имя:
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {couponData.user.name}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Email:
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {couponData.user.email}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {couponData.coupon.description && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2">
                        {couponData.coupon.description}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
            
            {couponData && !couponData.isUsed && (
              <Alert severity="warning">
                Внимание! После подтверждения купон будет отмечен как использованный и не может быть использован повторно.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirm}>
              Отмена
            </Button>
            {couponData && !couponData.isUsed ? (
              <Button 
                onClick={handleConfirmUse} 
                variant="contained" 
                color="primary"
                disabled={loading}
              >
                {loading ? 'Обработка...' : 'Подтвердить использование'}
              </Button>
            ) : (
              <Button 
                variant="outlined" 
                color="error"
                disabled
              >
                Купон уже использован
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
  );
};

export default ScanCoupon;
